import React from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { useBranding } from '../context/BrandingContext'

// Shared styling for the password-recovery auth screens (forgot + reset). Ported
// 1:1 from the redesign prototype (WebNest Forgot Password.html). The two-column
// shell + showcase styles mirror Login.jsx so all three auth screens are visually
// consistent; the rest is specific to the recovery flow (medallion, strength
// meter, requirement checklist, sent card, etc.). `.input` → `.ds-input` to reuse
// the app's input component.
export const AUTH_RESET_STYLES = `
body { overflow: auto; font-family: var(--font); background: var(--bg-0); color: var(--text); }
.auth-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  background:
    radial-gradient(1200px 800px at 0% -10%, rgba(124, 92, 255, 0.12), transparent 60%),
    radial-gradient(900px 600px at 110% 120%, rgba(91, 124, 255, 0.10), transparent 60%),
    var(--bg-0);
}
[data-theme="light"] .auth-page {
  background:
    radial-gradient(1200px 800px at 0% -10%, rgba(124, 92, 255, 0.07), transparent 60%),
    radial-gradient(900px 600px at 110% 120%, rgba(91, 124, 255, 0.06), transparent 60%),
    var(--bg-0);
}

/* ---------- Brand showcase panel ---------- */
.auth-showcase {
  position: relative; overflow: hidden; display: flex;
  flex-direction: column; padding: 44px 56px;
  border-right: 1px solid var(--border);
}
.showcase-grid {
  position: absolute; inset: 0;
  background-image: linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px);
  background-size: 46px 46px;
  mask-image: radial-gradient(900px 700px at 30% 25%, #000 0%, transparent 78%);
  -webkit-mask-image: radial-gradient(900px 700px at 30% 25%, #000 0%, transparent 78%);
  opacity: 0.6; pointer-events: none;
}
.showcase-orb {
  position: absolute; width: 460px; height: 460px; border-radius: 50%;
  filter: blur(20px);
  background: radial-gradient(circle at 50% 50%, var(--accent-soft), transparent 70%);
  top: -120px; right: -120px; pointer-events: none;
}
.brand-mark { position: relative; display: flex; align-items: center; gap: 11px; }
.brand-logo {
  width: 38px; height: 38px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #A78BFA, #5B7CFF);
  box-shadow: 0 8px 24px -10px var(--accent-glow);
}
.brand-wordmark { font-size: 17px; font-weight: 600; letter-spacing: -0.02em; color: var(--text); }
.brand-wordmark span { color: var(--accent-3); }
.showcase-body { position: relative; margin-top: auto; max-width: 460px; }
.showcase-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 500; letter-spacing: 0.09em; text-transform: uppercase;
  color: var(--text-muted); padding: 5px 11px 5px 8px;
  border: 1px solid var(--border); background: var(--surface); border-radius: 999px;
}
.showcase-eyebrow svg { color: var(--accent-3); }
.showcase-headline {
  margin: 22px 0 0; font-size: 40px; line-height: 1.08;
  font-weight: 600; letter-spacing: -0.03em; color: var(--text); text-wrap: balance;
}
.showcase-headline em {
  font-style: normal;
  background: linear-gradient(120deg, var(--accent-3), var(--accent-2));
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.showcase-sub { margin-top: 16px; color: var(--text-muted); font-size: 15px; line-height: 1.6; max-width: 420px; }
.showcase-features { display: flex; flex-direction: column; gap: 14px; margin-top: 34px; }
.showcase-feat { display: flex; align-items: center; gap: 13px; font-size: 13.5px; color: var(--text-soft); }
.feat-ico {
  width: 30px; height: 30px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  background: var(--accent-soft); color: var(--accent-3);
  border: 1px solid var(--border); flex-shrink: 0;
}
.showcase-foot {
  position: relative; margin-top: 40px; padding-top: 22px;
  border-top: 1px solid var(--border); display: flex; align-items: center; gap: 16px;
}
.showcase-quote { font-size: 13px; color: var(--text-muted); line-height: 1.5; }
.showcase-quote b { color: var(--text-soft); font-weight: 600; }
.avatar-stack { display: flex; }

/* ---------- Form panel ---------- */
.auth-form-wrap {
  position: relative; display: flex; align-items: center;
  justify-content: center; padding: 40px;
}
.auth-card { width: 100%; max-width: 388px; }
.auth-top {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px;
}

/* Back link */
.back-link {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 13px; font-weight: 500; color: var(--text-muted);
  text-decoration: none; padding: 7px 11px 7px 9px;
  border-radius: 9px; border: 1px solid transparent; transition: all 0.15s ease;
}
.back-link:hover { color: var(--text); background: var(--surface-hover); border-color: var(--border); }

/* Decorative key/lock medallion above headline */
.form-medallion {
  width: 52px; height: 52px; border-radius: 15px;
  display: flex; align-items: center; justify-content: center;
  background: var(--accent-soft); border: 1px solid var(--border-accent);
  color: var(--accent-3); box-shadow: 0 10px 30px -14px var(--accent-glow);
  margin-bottom: 20px;
}
.form-medallion.is-success {
  background: var(--success-soft); border-color: rgba(43, 217, 166, 0.4);
  color: var(--success); box-shadow: 0 10px 30px -14px rgba(43, 217, 166, 0.5);
}

.form-head h1 { font-size: 26px; font-weight: 600; letter-spacing: -0.025em; margin: 0; color: var(--text); }
.form-head p { margin: 10px 0 0; color: var(--text-muted); font-size: 14px; line-height: 1.6; }
.form-head p .email-pill { color: var(--text); font-weight: 500; white-space: nowrap; }

.field { margin-bottom: 16px; }
.field-label { display: flex; align-items: center; justify-content: space-between; margin-bottom: 7px; }
.field-label label { font-size: 12.5px; font-weight: 500; color: var(--text-soft); white-space: nowrap; }

.input-wrap { position: relative; }
.input-wrap .lead-ico {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--text-faint); display: flex; pointer-events: none;
}
.ds-input.with-ico { padding-left: 38px; height: 42px; }
.ds-input.with-ico.with-trail { padding-right: 42px; }
.toggle-pw {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
  color: var(--text-faint); border-radius: 7px; border: none; background: none; cursor: pointer;
}
.toggle-pw:hover { color: var(--text-soft); background: var(--surface-2); }

/* Inline field error */
.field-error {
  display: none; align-items: center; gap: 6px; margin-top: 7px;
  font-size: 12px; color: var(--danger);
}
.field.has-error .field-error { display: flex; }
.field.has-error .ds-input { border-color: rgba(255, 107, 107, 0.5); }

.btn-submit { width: 100%; height: 44px; font-size: 14px; font-weight: 600; border-radius: 11px; margin-top: 6px; }

/* Password strength meter */
.strength { margin: -4px 0 16px; }
.strength-bars { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.strength-bars i { height: 4px; border-radius: 99px; background: var(--surface-2); transition: background 0.25s ease; }
.strength-meta {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 8px; font-size: 11.5px; color: var(--text-muted);
}
.strength-meta .lvl { font-weight: 500; }
.strength[data-score="1"] .strength-bars i:nth-child(-n+1),
.strength[data-score="2"] .strength-bars i:nth-child(-n+2),
.strength[data-score="3"] .strength-bars i:nth-child(-n+3),
.strength[data-score="4"] .strength-bars i:nth-child(-n+4) { background: var(--lvl-color, var(--accent)); }

.reqs { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin: 2px 0 20px; }
.req { display: flex; align-items: center; gap: 7px; font-size: 12px; color: var(--text-muted); transition: color 0.15s ease; }
.req .req-dot {
  width: 15px; height: 15px; border-radius: 50%;
  border: 1px solid var(--border-strong);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; color: transparent; transition: all 0.15s ease;
}
.req.met { color: var(--text-soft); }
.req.met .req-dot { background: var(--success); border-color: transparent; color: #06140F; }

/* "Email sent" success block */
.sent-card {
  margin: 22px 0 8px; padding: 16px;
  border: 1px solid var(--border); background: var(--surface);
  border-radius: var(--radius); display: flex; align-items: center; gap: 13px;
}
.sent-card .env {
  width: 40px; height: 40px; border-radius: 11px;
  background: var(--bg-2); border: 1px solid var(--border);
  display: flex; align-items: center; justify-content: center;
  color: var(--accent-3); flex-shrink: 0;
}
.sent-card .env-txt { font-size: 12.5px; line-height: 1.5; color: var(--text-muted); }
.sent-card .env-txt b { color: var(--text-soft); font-weight: 600; }

/* Resend row */
.resend-row { margin-top: 20px; font-size: 13px; color: var(--text-muted); text-align: center; }
.resend-row button {
  color: var(--accent-3); font-weight: 500; font-size: 13px;
  padding: 0; border: none; background: none; cursor: pointer;
}
.resend-row button:hover { text-decoration: underline; }
.resend-row button:disabled { color: var(--text-faint); cursor: default; text-decoration: none; }

.help-list {
  margin: 24px 0 0; padding: 16px;
  border: 1px dashed var(--border-strong); border-radius: var(--radius); background: var(--surface);
}
.help-list h4 { margin: 0 0 10px; font-size: 12px; font-weight: 600; letter-spacing: 0.02em; color: var(--text-soft); }
.help-list ul { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 8px; }
.help-list li { display: flex; align-items: flex-start; gap: 9px; font-size: 12.5px; color: var(--text-muted); line-height: 1.5; }
.help-list li svg { color: var(--text-faint); margin-top: 2px; flex-shrink: 0; }

.form-foot { margin-top: 26px; text-align: center; font-size: 13px; color: var(--text-muted); }
.form-foot a, .form-foot button {
  color: var(--accent-3); text-decoration: none; font-weight: 500; font-size: 13px;
  padding: 0; border: none; background: none; cursor: pointer;
}
.form-foot a:hover, .form-foot button:hover { text-decoration: underline; }

.legal { margin-top: 34px; text-align: center; font-size: 11.5px; color: var(--text-faint); line-height: 1.6; }
.legal a { color: var(--text-muted); text-decoration: none; }
.legal a:hover { color: var(--text-soft); }

.auth-banner {
  padding: 10px 14px; border-radius: 10px; background: var(--danger-soft);
  color: var(--danger); font-size: 13px; margin-bottom: 16px;
}

/* ---------- Step entrance ---------- */
.step { display: block; }
@media (prefers-reduced-motion: no-preference) {
  .step.anim-in { animation: stepIn 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
}
@keyframes stepIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 940px) {
  .auth-page { grid-template-columns: 1fr; }
  .auth-showcase { display: none; }
}
`

// Brand lockup — mirrors Login.jsx so the showcase brand is identical across auth.
const BrandMark = ({ branding }) => {
  const logoSrc = branding.logoUrl || branding.logoUrlLight
  return (
    <div className="brand-mark">
      <div className="brand-logo">
        {logoSrc
          ? <img src={logoSrc} alt={branding.cmsName} style={{ width: 22, height: 22, objectFit: 'contain' }} />
          : (
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M8 8.5c1.6-2 6.4-2 8 0M8 12c1.6-2 6.4-2 8 0M8 15.5c1.6-2 6.4-2 8 0" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          )}
      </div>
      <div className="brand-wordmark">{branding.cmsName}</div>
    </div>
  )
}

// The "Account security" brand showcase shown on the left of every recovery screen.
const SecurityShowcase = ({ branding }) => (
  <section className="auth-showcase">
    <div className="showcase-grid" />
    <div className="showcase-orb" />

    <BrandMark branding={branding} />

    <div className="showcase-body">
      <div className="showcase-eyebrow">
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.4 8.3 8 10 4.6-1.7 8-5 8-10V5l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg>
        Account security
      </div>
      <h2 className="showcase-headline">Lost your key? Let's get you <em>back in</em> safely.</h2>
      <p className="showcase-sub">We'll send a secure, single-use link to your registered email. No password is ever shown or shared — your workspace stays locked down.</p>

      <div className="showcase-features">
        <div className="showcase-feat">
          <span className="feat-ico">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m4 7 8 6 8-6"/></svg>
          </span>
          Single-use reset links that expire in 60 minutes
        </div>
        <div className="showcase-feat">
          <span className="feat-ico">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="11" rx="2.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
          </span>
          Encrypted at rest — we never see your password
        </div>
        <div className="showcase-feat">
          <span className="feat-ico">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.4 8.3 8 10 4.6-1.7 8-5 8-10V5l-8-3Z"/></svg>
          </span>
          Every session re-verified after a reset
        </div>
      </div>

      <div className="showcase-foot">
        <div className="avatar-stack">
          {['SV', 'RP', 'TL', 'JR'].map((ini, i) => (
            <div key={i} className={`ds-avatar ds-avatar-${i + 1}`} style={{ width: 30, height: 30, fontSize: 11, marginLeft: i === 0 ? 0 : -8, boxShadow: '0 0 0 2px var(--bg-1)' }}>{ini}</div>
          ))}
        </div>
        <div className="showcase-quote">Trusted by <b>2,400+ studios</b> managing <b>38,000+ sites</b> worldwide.</div>
      </div>
    </div>
  </section>
)

const ThemeToggle = () => {
  const { toggleTheme } = useTheme()
  return (
    <button className="ds-btn ds-btn-icon ds-btn-ghost" onClick={toggleTheme} title="Toggle theme" type="button">
      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19"/>
      </svg>
    </button>
  )
}

// Two-column auth shell for the recovery screens. `showBack` keeps the
// "Back to sign in" link in the layout but hidden (used on the success step).
export const AuthShell = ({ children, showBack = true }) => {
  const branding = useBranding()
  return (
    <>
      <style>{AUTH_RESET_STYLES}</style>
      <div className="auth-page">
        <SecurityShowcase branding={branding} />
        <section className="auth-form-wrap">
          <div className="auth-card">
            <div className="auth-top">
              <Link className="back-link" to="/login" style={{ visibility: showBack ? 'visible' : 'hidden' }}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>
                Back to sign in
              </Link>
              <ThemeToggle />
            </div>
            {children}
          </div>
        </section>
      </div>
    </>
  )
}

// Map a recipient's email domain to its webmail inbox so "Open email app" lands
// somewhere useful. Falls back to Gmail (the most common consumer provider).
export const webmailInboxUrl = (email) => {
  const domain = (email.split('@')[1] || '').toLowerCase()
  const map = {
    'gmail.com': 'https://mail.google.com/mail/u/0/#search/from%3Anoreply',
    'googlemail.com': 'https://mail.google.com/mail/u/0/#search/from%3Anoreply',
    'outlook.com': 'https://outlook.live.com/mail/0/',
    'hotmail.com': 'https://outlook.live.com/mail/0/',
    'live.com': 'https://outlook.live.com/mail/0/',
    'msn.com': 'https://outlook.live.com/mail/0/',
    'yahoo.com': 'https://mail.yahoo.com/',
    'yahoo.co.uk': 'https://mail.yahoo.com/',
    'icloud.com': 'https://www.icloud.com/mail/',
    'me.com': 'https://www.icloud.com/mail/',
    'proton.me': 'https://mail.proton.me/u/0/inbox',
    'protonmail.com': 'https://mail.proton.me/u/0/inbox',
    'aol.com': 'https://mail.aol.com/',
  }
  return map[domain] || 'https://mail.google.com/mail/u/0/#search/from%3Anoreply'
}
